/**
 * Browser-side HMAC-SHA256 signer for the operator-side Create-session request,
 * mirroring the backend signing protocol exactly:
 *   stringToSign = "<unix_ts>.<METHOD> <fullpath>.<body>"
 *   X-Repackz-Timestamp: <unix_ts>
 *   X-Repackz-Signature: sha256=<hex(hmac_sha256(api_secret, stringToSign))>
 *
 * Used only by the API docs "Try it" session helper so an operator can run the
 * real signed flow (api_key + api_secret -> session_token) from this page. The
 * secret is held in memory only and never persisted.
 */
export interface SignedHeaders {
  'X-Repackz-Timestamp': string;
  'X-Repackz-Signature': string;
}

export async function signOperatorRequest(args: {
  apiSecret: string;
  method: string;
  fullpath: string;
  body?: string;
}): Promise<SignedHeaders> {
  const body = args.body ?? '';
  const timestamp = Math.floor(Date.now() / 1000).toString();
  const stringToSign = `${timestamp}.${args.method.toUpperCase()} ${args.fullpath}.${body}`;

  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(args.apiSecret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const digest = await crypto.subtle.sign('HMAC', key, enc.encode(stringToSign));
  const hex = Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return { 'X-Repackz-Timestamp': timestamp, 'X-Repackz-Signature': `sha256=${hex}` };
}
